import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../utils/languageUtils';
import StickerTabThemeSelector from './StickerTabThemeSelector';
import './StickersTab.css';

const StickersTab = ({ konvaController, canvasContainerRef, configData, onShowThemeSelection, selectedTheme, onThemeSelect, tabConfig = {} }) => {
  const { t } = useTranslation();
  const [stickers, setStickers] = useState([]);
  const [addingSticker, setAddingSticker] = useState(false);
  const [draggingUrl, setDraggingUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [targetRegion, setTargetRegion] = useState('upper');

  const loadStickersFromUrl = useCallback(async (stickerSetUrl) => {
    setLoading(true);
    try {
      if (!stickerSetUrl) throw new Error('Invalid sticker set URL');

      const response = await fetch(stickerSetUrl);
      if (!response.ok) throw new Error(`Failed to fetch stickers: ${response.status}`);

      const text = await response.text();

      const isImageUrl = (url) => {
        if (!url) return false;
        const lowerUrl = url.toLowerCase();
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
        return imageExtensions.some(ext => lowerUrl.endsWith(ext));
      };

      const lines = text.split('\n');
      const stickerUrls = lines
        .slice(1)
        .filter(line => line.trim())
        .map(line => {
          const parts = line.split(',');
          if (parts.length >= 2) {
            const url = parts[1].trim();
            return isImageUrl(url) ? url : null;
          }
          return null;
        })
        .filter(url => url);

      setStickers(stickerUrls);
      return stickerUrls;
    } catch (err) {
      console.error('Error loading stickers:', err);
      setStickers([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStickers = useCallback(async () => {
    setAddingSticker(false);
    try {
      if (selectedTheme?.selectedSticker) {
        const stickerSetUrl = selectedTheme.selectedSticker.StickerUrl || selectedTheme.selectedSticker.stickerSet?.Url || selectedTheme.selectedSticker.Url;
        if (stickerSetUrl) {
          await loadStickersFromUrl(stickerSetUrl);
          return;
        }
      }
      if (selectedTheme?.StickersCsv?.length > 0) {
        const firstSetUrl = selectedTheme.StickersCsv[0].Url;
        if (firstSetUrl) {
          await loadStickersFromUrl(firstSetUrl);
          return;
        }
      }
      if (configData?.Themes?.length > 0) {
        const firstTheme = configData.Themes[0];
        if (firstTheme.StickersCsv?.length > 0) {
          const firstSetUrl = firstTheme.StickersCsv[0]?.Url;
          if (firstSetUrl) {
            await loadStickersFromUrl(firstSetUrl);
            return;
          }
        }
      }
      setStickers([]);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stickers:', error);
      setStickers([]);
      setLoading(false);
    }
  }, [selectedTheme, configData, loadStickersFromUrl]);

  useEffect(() => {
    fetchStickers();
  }, [fetchStickers]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsTestMode(urlParams.get('test') === 'true');
  }, []);

  const handleThemeSelectFromSelector = (theme, stickerSets) => {
    if (onThemeSelect) {
      onThemeSelect({ ...theme, stickerSets: stickerSets || theme.stickerSets || [] });
    }
  };

  const handleStickerSetSelect = (stickerSet) => {
    if (!stickerSet) return;

    const stickerSetUrl = stickerSet.StickerUrl || stickerSet.stickerSet?.Url || stickerSet.Url;
    if (stickerSetUrl) {
      loadStickersFromUrl(stickerSetUrl);
    } else {
      console.error('Sticker set URL missing');
    }
  };

  const getFilteredStickers = () => stickers;

  const ALLOW_STICKER_TAB_THEME_SELECTION = tabConfig.allowStickerTabThemeSelection ?? true;

  const getCurrentViewConfig = () => {
      const viewId = typeof konvaController?.selectedView === 'string' 
          ? konvaController?.selectedView 
          : konvaController?.selectedView?.id;
      
      if (!viewId || !configData?.SupportedModels?.CommonProperties?.Views) return null;
      
      return configData.SupportedModels.CommonProperties.Views.find(v => v.id === viewId);
  };
  
  const currentViewConfig = getCurrentViewConfig();
  const hasClipRegions = currentViewConfig?.ClipRegions && Object.keys(currentViewConfig.ClipRegions).length > 0;

  // Helper to get localized name
  const getLocalizedName = (nameObj) => {
    if (!nameObj) return '';
    if (typeof nameObj === 'string') return nameObj;
    const lang = localStorage.getItem('site_language') || 'en';
    return nameObj[lang] || nameObj['en'] || '';
  };

  return (
    <div className="stickers-tab">
      <StickerTabThemeSelector
        themes={configData?.Themes || []}
        selectedTheme={selectedTheme}
        onThemeSelect={handleThemeSelectFromSelector}
        onStickerSetSelect={handleStickerSetSelect}
        allowThemeSelection={ALLOW_STICKER_TAB_THEME_SELECTION}
      />


      <div className="middle-option-container" style={{ display: 'hidden' }}>
        <div className="middle-option">
          {loading ? (
            <div className="stickers-loading">
              <div className="spinner" />
              <p>{t('stickers.loadingStickers')}</p>
            </div>
          ) : stickers.length === 0 ? (
            <div className="no-stickers-message">{t('stickers.noStickersAvailable')}</div>
          ) : (
            <div className="stickers-grid">
              {getFilteredStickers().map((url, index) => (
                <div key={index} className="sticker-item">
                  <button
                    className={`sticker-button ${addingSticker ? 'loading' : ''}`}
                    disabled={addingSticker}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', url);
                      setDraggingUrl(url);
                      const img = new Image();
                      img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
                      e.dataTransfer.setDragImage(img, 10, 10);
                    }}
                    onDragEnd={() => setDraggingUrl(null)}
                    onClick={() => {
                      if (!konvaController) {
                        console.error('konvaController not available');
                        return;
                      }
                      setAddingSticker(true);
                      konvaController.addImage(url, { name: 'sticker', draggable: true })
                        .then(sticker => {
                          if (!sticker) return;
                          const stage = konvaController.stage;
                          if (!stage) return;
                          const canvasWidth = stage.width();
                          const width = sticker.width();
                          const desiredWidth = canvasWidth * 0.10;
                          const scale = desiredWidth / width;
                          sticker.scaleX(scale);
                          sticker.scaleY(scale);
                          const isMobile = window.innerWidth <= 768;
                          if (isMobile) {
                            const productPos = konvaController._productImagePosition;
                            const productDim = konvaController._productImageDimensions;
                            if (productPos && productDim) {
                              const centerX = productPos.x + productDim.width / 2;
                              const centerY = productPos.y + productDim.height / 2;
                              sticker.x(centerX - (width * scale) / 2);
                              sticker.y(centerY - (sticker.height() * scale) / 2);
                            } else {
                              sticker.x((canvasWidth - width * scale) / 2);
                              sticker.y((stage.height() - sticker.height() * scale) / 2);
                            }
                          } else {
                            sticker.x((canvasWidth - width * scale) / 2);
                            sticker.y((stage.height() - sticker.height() * scale) / 2);
                          }
                          // Ensure the newly added sticker is selected so selection-aware toolbar buttons (Center X/Y)
                          // show immediately on first add.
                          try {
                            window.selectedKonvaNode = sticker;
                            window.dispatchEvent(new CustomEvent('SELECTION_CHANGED', { detail: { nodeType: 'image' } }));
                          } catch (e) {}
                          try {
                            if (typeof konvaController.addTransform === 'function') {
                              konvaController.addTransform(sticker, 'Design');
                            }
                          } catch (e) {}
                          sticker.getLayer().batchDraw();
                        })
                        .catch(console.error)
                        .finally(() => setAddingSticker(false));
                    }}
                    title={t('stickers.addToDesign')}
                  >
                    {addingSticker ? (
                      <div className="sticker-loading"><div className="spinner" /></div>
                    ) : (
                      <img src={url} alt={`${t('stickers.sticker')} ${index + 1}`} crossOrigin="anonymous" loading="lazy" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StickersTab;
